  import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import useForm from '@utils/useForm';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { get, isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { DeclarationForm } from '@models/SIO/Declaration.model';
import { updateDeclaration, updateReadDocs, completeStep, updatePdfPath } from '@redux/slices/SIO/step4Slice';
import { postDeclarationPDF, postSavingMyInfo } from '@api/index';
import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';
import { setLoadingState } from '@redux/slices/loadingSlice';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import IconDownload from '@assets/icon/download.svg';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Text from '@components/Text';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Link from '@components/Link';
import TextCollapsible from '@components/TextCollapsible';
import ContactBox from '@components/ContactBox';
import InformationBox from '@components/InformationBox';
import Button from '@components/Button';
import Divider from '@components/Divider';
import { validate } from './service';
import { fileLinkGen } from '@static/staticConstents';

const page = 'Declaration';

const convertDeclaration = (values: DeclarationForm) => {
  const convertValue = (value: boolean | undefined) => value === true ? 'Y' : 'N';
  return `1,${convertValue(values.policy)}|` +
    `2,${convertValue(values.summary)}|` +
    `3,${convertValue(values.term)}|` +
    `4,${convertValue(values.ped)}|` +
    `5,${convertValue(values.assured)}|` +
    `6,${convertValue(values.benefit)}|` +
    `7,${convertValue(values.completed)}`
}
const convertConsent = (values: any) => {
  let customerConsent = `SMS,${values['sms'] ? 'Y' : 'N'}|Phone,${values['phone'] ? 'Y' : 'N'}|Mail,${values['mail'] ? 'Y' : 'N'}`;
  return customerConsent;
}

export interface DeclarationProps {
  theme?: ITheme
}

const trackOnload = (values: any): void => {
  const consents = [
    values['sms'] && 'SMS',
    values['phone'] && 'Phone',
    values['mail'] && 'Mail',
  ].filter(o => o);
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  form.name = 'Declaration';
  acquisition.customer.marketingOptIn = consents;
  GA.trackPageLoad({ form, acquisition });
}
const trackFormSubmmit = (error: string = ''): void =>  {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

function Declaration(props: DeclarationProps) {
  console.log(`aviva - ${page}`);
  const history = useHistory();
  const theme: ITheme = useTheme();
  const dispatch = useAvivaDispatch();
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);

  const sqsQuotation = useAvivaSelector(state => state.sioStep1.sqsQuotation);
  const basicDetails = useAvivaSelector(state => state.sioStep1.basicDetails);
  const coverage = useAvivaSelector(state => state.sioStep1.coverage);
  const transactionId = useAvivaSelector(state=>state.sioStep1.transactionId)
  const quoteRawRequest = useAvivaSelector(state => state.sioStep1.quoteRawRequest);
  const myInfoForm = useAvivaSelector(state => state.sioStep2.myInfoForm);
  const retrievedMyInfo = useAvivaSelector(state => state.sioStep2.retrievedMyInfo);
  const isStep3Completed = useAvivaSelector(state => state.sioStep3.isCompleted);
  const initialValue = useAvivaSelector(state => state.sioStep4.declaration);
  const readDocs = useAvivaSelector(state => state.sioStep4.readDocs);

  const avivaContactUsUrl = get(window, 'AVIVA_VARIABLE_CONFIG.avivaContactUsUrl');

  const { values, formValid, handleChange, patchValue } = useForm(new DeclarationForm(initialValue), () => { }, validate);

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isReadPolicy, setIsReadPolicy] = useState<boolean>(false);
  const [isReadProduct, setIsReadProduct] = useState<boolean>(false);
  const [isReadTerms, setIsReadTerms] = useState<boolean>(false);
  const [isReadDeclare, setIsReadDeclare] = useState<boolean>(false);
  const [coverPolicy, setCoverPolicy] = useState<string>('');
  const [productSummary, setProductSummary] = useState<string>('');
  const [terms, setTerms] = useState<string>('');

  const getCoverPolicy = useCallback(async () => {
    const name = get(myInfoForm, 'fullName', '');
    const lifeprofiles: any = get(quoteRawRequest, 'lifeprofiles[0]');
    const savedRequest = { ...quoteRawRequest, lifeprofiles: [{ ...lifeprofiles, name: Helpers.capitalizeName(name) }] };
    const payload = { ...savedRequest };
    try {
      console.log(`check customerId: ${retrievedMyInfo?.id}`);
      const response = await postDeclarationPDF({ ...payload, customerId: retrievedMyInfo?.id, document: ['cp', 'bi'] });
      const policyUrl = get(response, 'data');
      if (policyUrl) {
        dispatch(updatePdfPath(policyUrl)); 
      }
      const url = './pdf/' + String(policyUrl).replace('/api/v1/pdf/', '');
      setCoverPolicy(url);
      console.debug(response, url);
    } catch (error) {
      console.error(error);
      // setCoverPolicy('./docs/illustration.pdf');
      window.location.reload();
    }
    
  }, [quoteRawRequest, myInfoForm, dispatch, retrievedMyInfo]);

  const downloadCoverPolicy = (url: any) => {
    console.log('policy full path', window.location.origin + window.location.pathname.replace('/index.html', '') + url.replace('./', '/'));
    const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + url.replace('./', '/');
    console.log('fullPath', fullPath);
    axios({
      url: fullPath,
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
      responseType: 'blob', // important
    }).then((response) => {
      let extension = 'pdf';
      let tempFileName = `cover_policy_illustration`
      let fileName = `${tempFileName}.${extension}`;
      var blob = new Blob([response.data as unknown as BlobPart], { type: "application/pdf" });
      saveAs(blob, fileName);
    });
    return false;
  }

  const loadPDFs = useCallback(async () => {
    dispatch(setLoadingState(true));
    await getCoverPolicy();
    const isCIRider = sqsQuotation?.isCIRider;
    const isPARider = sqsQuotation?.isPARider;
    if (isCIRider && isPARider) {
      setProductSummary( fileLinkGen("sio","DirectsalesSIO-PS-CI-PA.pdf"));
      setTerms(fileLinkGen("sio","DirectsalesSIO-Terms-CI-PA.pdf")) //'./docs/sio/Terms-CI-PA.pdf'); //fileLinkGen("sio","Terms-CI-PA.pdf")
    } else if (isCIRider) {
      setProductSummary(fileLinkGen("sio","DirectsalesSIO-PS-CI.pdf" ));
      setTerms(fileLinkGen("sio","DirectsalesSIO-Terms-CI.pdf")) //'./docs/sio/Terms-CI.pdf') //fileLinkGen("sio","Terms-CI.pdf"));
    } else if (isPARider) {
      setProductSummary(fileLinkGen("sio","DirectsalesSIO-PS-PA.pdf")) //'./docs/sio/PS-PA.pdf');
      setTerms(fileLinkGen("sio","DirectsalesSIO-Terms-PA.pdf"));
    } else {
      setProductSummary(fileLinkGen("sio","DirectsalesSIO-PS.pdf")) //'./docs/sio/PS.pdf');
      setTerms(fileLinkGen("sio","DirectsalesSIO-Terms.pdf")) //'./docs/sio/Terms.pdf');
    }
    dispatch(setLoadingState(false));

  }, [dispatch, getCoverPolicy, sqsQuotation]);

  const updateMyInfo = (async () => {
    const id = retrievedMyInfo?.id || 0;
    const declaration = convertDeclaration(values);

    console.debug(values);
    let customerConsent = convertConsent(values);
    console.log('customerConsent', customerConsent);
    
    const payload: IMyInfoQuestionRequest = { id, customerConsent, declaration, transactionId };
    postSavingMyInfo(payload).then(res => {
      console.debug('postSavingMyInfo', res);
      history.push('/sio/review');
    }, error => {
      console.error(error);
      history.push('/sio/review');
    })
  });

  const onClickContinue = () => {
    trackOnload(values);
    trackFormSubmmit();
    dispatch(updateDeclaration(values));
    dispatch(updateReadDocs({ isReadPolicy, isReadProduct, isReadTerms, isReadDeclare }));
    dispatch(completeStep(true));
    updateMyInfo();
  }
  const onBack = useCallback((step: number = 1) => {
    history.push(`/sio/about-you?step=${step}`)
  }, [history])

  const gotoContact = () => {
    history.push('/sio/contact')
  }

  const checkboxHandleChange = (e: any) => {
    const { value, name } = e.target
    console.log(e.target, { [name]: value });
    patchValue({ [name]: value });
  }

  useEffect(() => {
    if (!isPageLoaded) {
      if (!isStep3Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        onBack(0);
      }
      if (readDocs) {
        const { isReadDeclare, isReadPolicy, isReadProduct, isReadTerms } = readDocs;
        setIsReadDeclare(isReadDeclare);
        setIsReadPolicy(isReadPolicy);
        setIsReadProduct(isReadProduct);
        setIsReadTerms(isReadTerms);
      }
      if (basicDetails && coverage && isEmpty(coverPolicy)) {
        loadPDFs();
        console.log('sqsQuotation', sqsQuotation)
      }
      trackOnload(values);
      setIsPageLoaded(true);
    } 
    
  }, [getCoverPolicy, sqsQuotation, readDocs, dispatch, basicDetails, coverage, coverPolicy, loadPDFs, isPageLoaded, isStep3Completed, onBack, values]);


  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={4} label="Declaration" />
          <Heading> Declaration </Heading>
        </FullGrid>
        <LeftGrid>

          <Heading variant="label" margin="0 0 30px"> Please read and agree to all of the following to proceed. </Heading>
          <InformationBox hasClose>
            <Text>Please ensure that you have <b>clicked on the links</b> and read the documents fully before selecting the checkboxes.</Text>
          </InformationBox>
          <CheckboxList margin="0 0 40px">
            <Checkbox name="policy" margin="0 0 30px" checked={values['policy']} onChange={handleChange} disabled={!isReadPolicy}>
              <Text> I have downloaded, fully read and understood the&nbsp;
                <Link url={coverPolicy} hasDownload={true} downloadCallback={() => { downloadCoverPolicy(coverPolicy); }} target="_blank" rel="noopener noreferrer" callback={() => { setIsReadPolicy(true) }} isClicked={isReadPolicy} isCheckClicked text="cover page and policy illustration*" icon={IconDownload} slotIcon="end" />
              </Text>
            </Checkbox>
            <Checkbox name="summary" margin="0 0 30px" checked={values['summary']} onChange={handleChange} disabled={!isReadProduct}>
              <Text>
                I have downloaded, fully read and understood the&nbsp;
                <Link download="product-summary" text="product summary" target="_blank" rel="noopener noreferrer" url={productSummary} isClicked={isReadProduct}  callback={() => { setIsReadProduct(true) }} isCheckClicked icon={IconDownload} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="term" margin="0 0 30px" checked={values['term']} onChange={handleChange} disabled={!isReadTerms}>
              <Text>
                I have downloaded, fully read and understood the&nbsp;
                <Link download="terms-and-conditions" text="policy terms and conditions" target="_blank" rel="noopener noreferrer" url={terms}  isClicked={isReadTerms}  callback={() => { setIsReadTerms(true) }} isCheckClicked icon={IconDownload} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="ped" margin="0 0 30px" checked={values['ped']} onChange={handleChange} disabled={!isReadDeclare}>
              <Text>
                I have downloaded, fully read and agreed to the statements described in this&nbsp;
                <Link target="_blank" rel="noopener noreferrer" url={fileLinkGen("sio","DirectsalesSIO-Declaration.pdf")} download="declaration" isClicked={isReadDeclare} callback={() => { setIsReadDeclare(true) }} isCheckClicked text="online purchase declarations" icon={IconDownload} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="assured" margin="0 0 30px" checked={values['assured']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I am not a Politically Exposed Person (PEP) or a family member/close associate of a PEP.</Text>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0">
                <Text margin="0 0 10px">
                  “PEP” means a natural person who holds or has held prominent public functions, whether locally or internationally, which includes the roles held by a head of state, a head of government, government ministers, senior civil or public servants, senior judicial or military officials, senior executives of state owned corporations, senior political party officials, members of the legislature and senior management of international organisations.
                  <br />
                  <br />
                  “Family Member” means a parent, step-parent, child, step-child, adopted child, spouse, sibling, step-sibling and adopted sibling of the PEP.
                  <br />
                  <br />
                  “Close Associate” means a natural person who is closely connected to a PEP, either socially or professionally.
                </Text>
              </TextCollapsible>
            </Checkbox>
            <Checkbox name="benefit" margin="0 0 30px" checked={values['benefit']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> There is no beneficial ownership arrangement under this application.</Text>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0" expanded>
                <Text margin="0 0 10px">
                  “Beneficial Owner” means the natural person who ultimately owns or controls a customer or the natural person on whose behalf a transaction is conducted or business relations are established and includes any person who exercises ultimate effective control over a legal person or legal arrangement.
                </Text>
              </TextCollapsible>
            </Checkbox>
            <Checkbox name="completed" margin="0 0 30px" checked={values['completed']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare}><Text> I have completed and disclosed fully and truthfully all the information requested for in this application. </Text></Checkbox>
            <Checkbox name="understand" margin="0 0 30px" checked={values['understand']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I understand that I will receive my policy, any endorsements and communications electronically once they are ready. I understand that I may log on to MySinglife to opt for my preferred document option and may switch between e-documents and printed documents.</Text>
            </Checkbox>
          </CheckboxList>
          <Text margin="0 0 30px">
            <strong>*Important note:</strong> As this is a product with no financial advice, there is <u>no</u> associated distribution costs in relation to the provision of advice.
          </Text>

          <Divider size={1} variant="dashed" color={theme.colors.grey[700]} margin="0 0 40px" />
          <Heading variant="sub">Stay in touch with us!</Heading>
          <Text margin="0 0 30px">We’ll update you on special marketing offers, promotions, and information about Singlife’s products and services which may be of interest to you. For details, please refer to <Link target="_blank" rel="noopener noreferrer" url="https://singlife.com/en/pdpa#dpp" text="Singlife’s data protection policy"></Link>. </Text>
          <Text margin="0 0 15px">You consent to receiving marketing updates via:</Text>
          <CheckboxList margin="0 0 24px">
            <Checkbox variant="border" name="mail" onChange={e => checkboxHandleChange(e)}> <Text>Mail or Email</Text> </Checkbox>
            <Checkbox variant="border" name="sms" onChange={e => checkboxHandleChange(e)}> <Text>Messages on any messaging platform (including SMS)</Text> </Checkbox>
            <Checkbox variant="border" name="phone" onChange={e => checkboxHandleChange(e)}> <Text>Telephone Call</Text> </Checkbox>
          </CheckboxList>
          <Text margin="0 0 40px">If you wish to withdraw your contest, you may do so at <Link target="_blank" rel="noopener noreferrer" url={avivaContactUsUrl} text="Singlife’s website" /> at anytime.
          </Text>
          <InformationBox hasClose margin="0 0 40px">
            <Text> If you’re unable to complete this Declaration, please leave your contact details <Link text="here" variant="button" onClick={gotoContact} /> and our financial adviser representative will be in touch with you shortly. </Text>
          </InformationBox>
          <Button label="Continue" disabled={!formValid} onClick={onClickContinue} margin="0 0 20px" />
          <Button variant="secondary" onClick={() => onBack(1)} label="Back" margin="0 0 40px" />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid>
      </ContainerGrid>
    </Container>
  );
};

export default Declaration;